import apiReader from '../store/readHelpers/ApiReader';

export function defaultResponseValidator(response) {
    return response?.success !== false;
}

export function isUnrecoverableOffline() {
    return apiReader.isOnline();
}

export function isOnline() {
    return apiReader.isOnline();
}

export const SKIPPED = { SKIPPED: true };
export const NO_ERROR = { NO_ERROR: true };
export const NO_VALUE = { NO_VALUE: true };

class AsyncRetryer {
    /**
     * @param {function} functionToTry Any function that returns a promise.
     * @param {int} maxTries
     * @param {int} retryInterval The number of milliseconds to wait between each retry.
     * @param {boolean} returnLastResponse Returns the last received response, even if not acceptable, after the final try.
     * @param {boolean} abortIfDisconnectedStatus if retryer should abort if connection status is disconnected
     * @param {function} responseValidator called to check if a response is acceptable for returning.
     * @param {function} retryableStateChecker called to check if it is meaningful to retry.
     */
    constructor(
        functionToTry,
        maxTries,
        retryInterval,
        returnLastResponse,
        abortIfDisconnectedStatus = false,
        responseValidator = defaultResponseValidator,
        retryableStateChecker = isOnline,
    ) {
        this.functionToTry = functionToTry;
        this.maxTries = maxTries;
        this.startedTries = 0;
        this.retryInterval = retryInterval;
        this.returnLastResponse = returnLastResponse;
        this.responseValidator = responseValidator;
        this.stateAllowsForRetry = retryableStateChecker;
        this.abortIfDisconnectedStatus = abortIfDisconnectedStatus;
        this.timeoutHandle = null;
        this.isCancelled = false;
    }

    cancel() {
        this.isCancelled = true;
        clearTimeout(this.timeoutHandle);
    }

    canTryAgain() {
        return this.startedTries < this.maxTries;
    }

    isValidResponse(response) {
        return this.responseValidator(response) && ![NO_VALUE, SKIPPED].includes(response);
    }

    async callFunctionIfStateAllows() {
        if (this.stateAllowsForRetry()) {
            return this.functionToTry();
        }
        return SKIPPED;
    }

    async scheduleCall(delay) {
        return new Promise((resolve, reject) => {
            this.timeoutHandle = setTimeout(() => {
                this.callFunctionIfStateAllows()
                    .then(result => resolve(result))
                    .catch(error => reject(error));
            }, delay);
        });
    }

    async start() {
        return this.makeATry()
            .catch((error) => {
                console.log('Error from makeATry():', error.message);
                return Promise.reject(error);
            });
    }

    async makeATry() {
        let returnedValue = NO_VALUE;
        let caughtError = NO_ERROR;
        this.startedTries = this.startedTries + 1;

        try {
            const delay = this.startedTries > 1 ? this.retryInterval : 0;
            returnedValue = await this.scheduleCall(delay);
        } catch (error) {
            caughtError = error;
            if (error?.message === 'AbortError') {
                return Promise.reject({
                    message: 'Aborting AsyncRetryer',
                    startedTries: this.startedTries,
                });
            }
        }

        if (this.isCancelled) {
            return Promise.reject({
                message: 'Canceled',
                startedTries: this.startedTries,
            });
        }

        if (this.isValidResponse(returnedValue)) {
            return {
                data: returnedValue,
                startedTries: this.startedTries,
            };
        }

        if (returnedValue?.SKIPPED === true) {
            if (this.abortIfDisconnectedStatus && apiReader.isDisconnected()) {
                console.error('Aborting retry as client was disconnected!');
                return Promise.reject({
                    DISCONNECTED: true,
                    startedTries: this.startedTries,
                });
            }
            console.log('Request was skipped, do not let the try count.');
            this.startedTries = this.startedTries - 1;
        }

        if (!this.canTryAgain()) {
            if (caughtError !== NO_ERROR) {
                throw caughtError;
            }
            if (this.returnLastResponse) {
                return {
                    data: returnedValue,
                    startedTries: this.startedTries,
                };
            }
            return Promise.reject({
                data: returnedValue,
                startedTries: this.startedTries,
            });
        }

        return this.makeATry();
    }
}

export default AsyncRetryer;
